import type { CrmTicket, Deal, TicketDeviceRequest } from '@cobliteam/common-types';
import camelcaseKeys from 'camelcase-keys';
import decamelizeKeys from 'decamelize-keys';
import type {
  Configuration,
  CrmReportProblemTicketsDto,
  CrmSchedulingTicketSummaryDto,
  CrmTicketsDto,
  IRequest,
  LastAssociationDeviceVehicle,
  UpdateCrmSchedulingFormDto,
} from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import { ticketAPIToReportProblemTicket, ticketAPIToTicket } from './APITransformer';

export class CrmRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getSchedulingTickets = async (): Promise<CrmTicketsDto> => {
    const requestData: IRequest = {
      endpoint: '/crm/tickets/scheduling',
      method: HttpMethod.GET,
    };
    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data, { deep: true });
  };

  public getSchedulingTicketsSummary = async (): Promise<CrmSchedulingTicketSummaryDto> => {
    const requestData: IRequest = {
      endpoint: '/crm/tickets/scheduling-summary',
      method: HttpMethod.GET,
    };
    return this.requester.request(requestData).then((response) => camelcaseKeys(response.data, { deep: true }));
  };

  public getDevolutionTickets = async (): Promise<CrmTicketsDto> => {
    const requestData: IRequest = {
      endpoint: '/crm/tickets/devolution',
      method: HttpMethod.GET,
    };

    const response = await this.requester.request(requestData);

    const tickets = response.data.tickets.map(ticketAPIToTicket);
    const total = response.data.total;

    return { tickets, total };
  };

  public getLastAssociations = async (devicesIds: any): Promise<LastAssociationDeviceVehicle[]> => {
    const requestData: IRequest = {
      endpoint: '/v1/devices/last-associations/',
      method: HttpMethod.POST,
      body: JSON.stringify(devicesIds),
    };

    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data, { deep: true });
  };

  public getShippingTickets = async (): Promise<CrmTicketsDto> => {
    const requestData: IRequest = {
      endpoint: '/crm/tickets/shipping',
      method: HttpMethod.GET,
    };

    const response = await this.requester.request(requestData);

    const tickets = response.data.tickets.map(ticketAPIToTicket);
    const total = response.data.total;

    return { tickets, total };
  };

  public notResponsible = (ticket: string | undefined): Promise<any> => {
    const requestData: IRequest = {
      endpoint: `/crm/tickets/scheduling/${ticket}/not-responsible`,
      method: HttpMethod.PUT,
    };

    return this.requester.request(requestData);
  };

  public responsible = (ticket: string | undefined): Promise<any> => {
    const requestData: IRequest = {
      endpoint: `/crm/tickets/scheduling/${ticket}/responsible`,
      method: HttpMethod.PUT,
    };

    return this.requester.request(requestData);
  };

  public confirmInstallation = (ticket: string | undefined): Promise<any> => {
    const requestData: IRequest = {
      endpoint: `/crm/tickets/scheduling/${ticket}/confirm`,
      method: HttpMethod.PUT,
    };

    return this.requester.request(requestData);
  };

  public cancelInstallation = (ticket: string | undefined): Promise<any> => {
    const requestData: IRequest = {
      endpoint: `/crm/tickets/scheduling/${ticket}/cancel`,
      method: HttpMethod.PUT,
    };

    return this.requester.request(requestData);
  };

  public updateSchedulingTicketForm = (formRequest: UpdateCrmSchedulingFormDto): Promise<any> => {
    const requestData: IRequest = {
      endpoint: '/crm/tickets/scheduling-by-form',
      body: JSON.stringify(decamelizeKeys(formRequest)),
      method: HttpMethod.PUT,
    };

    return this.requester.request(requestData);
  };

  public rateInstallation = (ticketId: string | undefined): Promise<any> => {
    const requestData: IRequest = {
      endpoint: `/crm/tickets/scheduling/${ticketId}/rate`,
      method: HttpMethod.PUT,
    };

    return this.requester.request(requestData);
  };

  public reportProblem = (deviceId: string, ticketDeviceRequest: TicketDeviceRequest): Promise<CrmTicket> => {
    const requestData = {
      endpoint: '/crm/tickets/devices',
      method: HttpMethod.POST,
      body: JSON.stringify(decamelizeKeys({ deviceId, ...ticketDeviceRequest })),
    };

    return this.requester.request(requestData).then();
  };

  public searchTicket = async (deviceId: string): Promise<CrmReportProblemTicketsDto> => {
    const requestData: IRequest = {
      endpoint: `/crm/tickets/devices/${deviceId}`,
      method: HttpMethod.GET,
    };

    const response = await this.requester.request(requestData);

    return {
      tickets: response.data.tickets.map(ticketAPIToReportProblemTicket),
      total: response.data.total,
    };
  };

  public getHubpotDealId = (fleetId: string): Promise<Deal> => {
    const requestData: IRequest = {
      endpoint: `/crm/deals/${fleetId}`,
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => camelcaseKeys(response.data, { deep: true }));
  };
}

export default CrmRequester;
